import { useState } from 'react';

import { DocumentFileUpload, ProfileOptionAnswer, ConfirmDelete } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { familyApi } from '@backrs_ui/grow-ui/src/redux/family/family.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { usePostProfileOptionAnswerMutation } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import {
  profileApi,
  useFetchUserProfileInfoQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { setUser } from '@backrs_ui/grow-ui/src/redux/user/user';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Box, Avatar, Typography, SxProps, Theme } from '@mui/material';

import { ImageUploadDialog } from './ImageUploadDialog';
import { ProfileFieldConfig } from '../types/profile.types';

const STRINGS = appStrings.modules.profile.edit;

type ProfileImageFieldProps = Readonly<{
  field: ProfileFieldConfig;
}>;

export const ProfileImageField = ({ field }: ProfileImageFieldProps) => {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const [postProfileOptionAnswer] = usePostProfileOptionAnswerMutation();

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);

  const { data: profile } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);

  const [bannerImage, setBannerImage] = useState<string | null>(null);

  const profileImage = profile?.profile?.publicProfile?.profile_image;
  const profileBanner = profile?.profile?.publicProfile.profile_banner_image;
  const isFieldProfileImage = field.key === 'profileImage';
  const imageToUse = isFieldProfileImage ? profileImage : profileBanner;

  const handleRefetch = () => {
    // now invalidate the user profile to refresh the image
    dispatch(profileApi.util.invalidateTags(['UserProfile']));
    dispatch(familyApi.util.invalidateTags(['FamilyAccount']));
  };

  const handleOpenUploadDialog = () => {
    setIsUploadDialogOpen(true);
  };

  const handleFileSelect = (files: File | File[]) => {
    // take only the first file since we only allow one image upload
    const file = Array.isArray(files) ? files[0] : files;

    if (bannerImage) {
      URL.revokeObjectURL(bannerImage);
    }

    const newBannerImage = URL.createObjectURL(file);

    setBannerImage(newBannerImage);
    setIsUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    handleRefetch(); // get the new images
    setIsUploadDialogOpen(false);

    if (bannerImage) {
      URL.revokeObjectURL(bannerImage);
      setBannerImage(null);
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    const type = isFieldProfileImage ? 'profile_media_id' : 'profile_banner_media_id';

    const profileOptionAnswer: ProfileOptionAnswer = {
      user_id: user.id,
      question_id: type,
      category_id: type,
      value: null,
    };

    await postProfileOptionAnswer({
      profileOptionAnswers: [profileOptionAnswer],
      postKey: type,
    }).unwrap();

    handleRefetch();

    dispatch(
      setUser({
        ...user,
        ...(isFieldProfileImage
          ? { profile_image: undefined }
          : { profile_banner_image: undefined }),
      }),
    );

    setOpenDialog(false);
  };

  return (
    <Box sx={avatarActionsSx}>
      {/* PROFILE HEADSHOT */}
      {isFieldProfileImage && imageToUse && (
        <Avatar src={imageToUse} sx={avatarSx}>
          <AccountCircleIcon />
        </Avatar>
      )}

      {/* PROFILE BANNER */}
      {!isFieldProfileImage && imageToUse && <Box component="img" src={imageToUse} sx={bannerSx} />}

      {/* UPLOADER WHEN NO IMAGE PRESENT */}
      {!imageToUse ? (
        <DocumentFileUpload
          onFileSelect={handleFileSelect}
          acceptedFileTypes={['.png', '.jpeg', '.jpg']}
          additionalText={null}
          inputId={isFieldProfileImage ? 'profile-image-upload' : 'profile-banner-upload'}
          multiple={false}
        />
      ) : (
        // UPLOADER WHEN IMAGE IS PRESENT
        <Box sx={imageActionsSx}>
          <Typography
            id="profile-image-update-link"
            sx={linkSx}
            variant="text-sm-semibold"
            color={({ backrs }) => backrs.colors['Colors/Text/text-brand-secondary']}
            onClick={handleOpenUploadDialog}
          >
            {STRINGS.update}
          </Typography>

          <Typography
            id="profile-image-delete-link"
            sx={linkSx}
            variant="text-sm-semibold"
            color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            onClick={() => setOpenDialog(true)}
          >
            {STRINGS.delete}
          </Typography>
        </Box>
      )}

      <ImageUploadDialog
        open={isUploadDialogOpen}
        onClose={handleCloseUploadDialog}
        type={isFieldProfileImage ? 'profile_media_id' : 'profile_banner_media_id'}
        preLoadedImage={bannerImage}
      />

      <ConfirmDelete
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        handleDelete={handleDelete}
        headerText={STRINGS.deleteConfirmHeader(isFieldProfileImage)}
        bodyText={STRINGS.deleteConfirmBody(isFieldProfileImage)}
      />
    </Box>
  );
};

const avatarSx: SxProps<Theme> = {
  width: 72,
  height: 72,
};

const avatarActionsSx: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
  flexDirection: 'column-reverse',
  '@container (min-width: 900px)': {
    flexDirection: 'row',
  },
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const linkSx: SxProps<Theme> = {
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.5,
  },
};

const bannerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  '@container (min-width: 900px)': {
    width: '75%',
  },
  height: '100%',
  objectFit: 'cover',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const imageActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  width: '100%',
  gap: backrs.spacing['spacing-md'],
  '@container (min-width: 900px)': {
    gap: backrs.spacing['spacing-xl'],
  },
});
